import * as React from "react";
import { Grid, GridColumn, Input } from "semantic-ui-react";
import "./project-edit.css";
import MyDatePicker from "../../../components/date-picker/date-picker";
import LabeledInput from "../../../components/labeled-input/labeled-input";
import LabeledTextArea from "../../../components/labeled-textarea/labeled-textarea";
import { KeyValueStore } from "../../../stores/key-value/key-value-store";
import { Contact } from "../../../stores/contact/contact-store";
import { DezoningProject } from "../../../stores/project/project";
import LabeledValue from "../../../components/labeled-value/labeled-value";
import { Municipality } from "../../../stores/municipality/municipality-store";
import { getDesc, getLabel } from "../../../reflection/reflection";

export default function DezoningEdit({
                                         project,
                                         municipalities,
                                         contacts,
                                         keyValueStore
                                     }: {
    project: DezoningProject;
    municipalities: Municipality[];
    contacts: Contact[];
    keyValueStore: KeyValueStore
}): JSX.Element {
    return (
        <div>
            <Grid columns={2} relaxed={true} stackable={true}>
                <GridColumn largeScreen={16} widescreen={8}>
                    <LabeledValue
                        label={getLabel(project, "fullType")}
                        value={project.type}
                        required={true}/>
                    <LabeledInput
                        label="Name"
                        inputElement={
                            <Input
                                type="text"
                                name="name"
                                defaultValue={project.name}
                                maxLength={50}
                            />
                        }
                        required={true}
                    />
                    <LabeledInput
                        label={getLabel(project, "municipalityName")}
                        desc={getDesc(project, "municipalityName")}
                        inputElement={
                            <select name="municipality" className="ui selection dropdown">
                                <option disabled selected={project.municipality === undefined}>
                                    -- Auswählen --
                                </option>
                                {municipalities.map((m) => (
                                    <option
                                        key={m.bfsNr}
                                        value={m.bfsNr}
                                        selected={project.municipality?.bfsNr === m.bfsNr}
                                    >
                                        {m.name}
                                    </option>
                                ))}
                            </select>
                        }
                        required={true}
                    />
                    <LabeledInput
                        label={getLabel(project, "place")}
                        desc={getDesc(project, "place")}
                        inputElement={<Input type="text" name="place" defaultValue={project.place}/>}
                    />
                    <LabeledInput
                        label={getLabel(project, "revision")}
                        desc={getDesc(project, "revision")}
                        inputElement={
                            <select name="revision" className="ui selection dropdown">
                                <option
                                    selected={project.projectDto.revision === null}
                                    disabled>
                                    -- Auswählen --
                                </option>
                                {keyValueStore.revisionTypes.map((r) => (
                                    <option
                                        key={r.key}
                                        value={r.key}
                                        selected={project.projectDto.revision === r.key}
                                    >
                                        {r.value}
                                    </option>
                                ))}
                            </select>
                        }
                        required={true}
                    />
                    <LabeledInput
                        label={getLabel(project, "status")}
                        inputElement={
                            <select name="status" className="ui selection dropdown">
                                {keyValueStore.projectStatuses.map((s) => (
                                    <option
                                        key={s.key}
                                        value={s.key}
                                        selected={project.projectDto.status === s.key}
                                    >
                                        {s.value}
                                    </option>
                                ))}
                            </select>
                        }
                        required={true}
                    />
                    <LabeledInput
                        label={getLabel(project, "governmentCouncilDecisionNr")}
                        desc={getDesc(project, "governmentCouncilDecisionNr")}
                        inputElement={<Input type="text" name="governmentCouncilDecisionNr"
                                             defaultValue={project.governmentCouncilDecisionNr}/>}
                    />
                    <LabeledInput
                        label={getLabel(project, "enactmentDateDisplayText")}
                        desc={getDesc(project, "enactmentDateDisplayText")}
                        inputElement={<MyDatePicker name="enactmentDate" value={project.enactmentDate}/>}
                    />
                    <LabeledInput
                        label={getLabel(project, "approvedCreationArea")}
                        desc={getDesc(project, "approvedCreationArea")}
                        inputElement={<Input name="approvedCreationArea" type="number"
                                             defaultValue={project.approvedCreationArea}/>}
                    />
                    <LabeledInput
                        label={getLabel(project, "landOwnerFullName")}
                        desc={getDesc(project, "landOwnerFullName")}
                        inputElement={
                            <select name="landOwnerId" className="ui selection dropdown">
                                <option
                                    key={-1} value=""
                                    selected={project.landOwner === undefined}>
                                    -- Auswählen --
                                </option>
                                {contacts.map(c => (
                                    <option
                                        key={c.id}
                                        value={c.id}
                                        selected={project.landOwner?.id === c.id}
                                    >{`${c.firstName} ${c.lastName}`}</option>
                                ))}
                            </select>
                        }
                    />
                    <LabeledInput
                        label={getLabel(project, "cmiDocumentsRawiPre")}
                        desc={getDesc(project, "cmiDocumentsRawiPre")}
                        inputElement={<Input type="text" name="cmiNrRawiPre"
                                             defaultValue={project.cmiDocumentsRawiPre.join(", ")}/>}
                        required={true}
                    />
                    <LabeledInput
                        label={getLabel(project, "cmiDocumentsUwePre")}
                        desc={getDesc(project, "cmiDocumentsUwePre")}
                        inputElement={<Input type="text" name="cmiNrUwePre"
                                             defaultValue={project.cmiDocumentsUwePre.join(", ")}/>}
                    />
                    <LabeledInput
                        label={getLabel(project, "cmiDocumentsRawiApproved")}
                        desc={getDesc(project, "cmiDocumentsRawiApproved")}
                        inputElement={<Input type="text" name="cmiNrRawiApproved"
                                             defaultValue={project.cmiDocumentsRawiApproved.join(", ")}/>}
                    />
                    <LabeledInput
                        label={getLabel(project, "cmiDocumentsUweApproved")}
                        desc={getDesc(project, "cmiDocumentsUweApproved")}
                        inputElement={<Input type="text" name="cmiNrUweApproved"
                                             defaultValue={project.cmiDocumentsUweApproved.join(", ")}/>}
                    />
                </GridColumn>
                <GridColumn>
                    <LabeledInput
                        label={getLabel(project, "geometrySource")}
                        desc={getDesc(project, "geometrySource")}
                        inputElement={<Input type="text" maxLength={50} name="geometrySource"
                                             defaultValue={project.geometrySource}/>}
                    />
                    <LabeledInput
                        label={getLabel(project, "geometrySourceDateDisplayText")}
                        desc={getDesc(project, "geometrySourceDateDisplayText")}
                        inputElement={<MyDatePicker name="geometrySourceDate" value={project.geometrySourceDate}/>}
                    />
                    <LabeledTextArea label={getLabel(project, "generalRemarks")} name="generalRemarks"
                                     defaultValue={project.generalRemarks}/>
                </GridColumn>
            </Grid>
        </div>
    );
}
